import { FC, useRef, useEffect, useState } from "react";
import { useDataTableStore } from "../../stores/DataTableStore";
import { useFormik } from "formik";
import * as Yup from 'yup';
import Swal from "sweetalert2";
import clsx from "clsx";
import useAccessToken from "../../hooks/AccessToken";
import useSWR from "swr";
import axios from "axios";
import { insertProgress } from "../../api/ProgressesCRUD";
import Select from "react-select";
import { nanoid } from "@reduxjs/toolkit";
import { SelectOption } from "../../interfaces/SelectOption";
import { SketchPicker } from "react-color";
import { KTSVG } from "../../_metronic/helpers";
import { handleCaps, randomColor } from "../../functions/general";
import { Approval, SubProgress } from "../../interfaces/Progress";
import { useIntl } from "react-intl";
import { useSettingsStore } from "../../stores/SettingsStore";
import { Switch } from "@mui/material";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import { SettingMultiPipelines } from "../../interfaces/Settings";
import { usePipelineStore } from "../../stores/PipelineStore";
import { useUserStore } from "../../stores/UserStore";
import { DndContext, useSensor, useSensors, MouseSensor, TouchSensor, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import VirtualizedSelect, { VirtualizedSelectOption } from "../Select/SelectVirtualized";
import { Toggle } from "rsuite";
import { getAllOption } from "../../functions/getOptions";
import { API_URL } from "../../api/SettingCRUD";
import { mutate } from "swr";
import { TableToggleProgress } from "./TableProgress";

const AddProgress: FC = () => {
    const intl = useIntl()
    const token = useAccessToken()
    const { settings } = useSettingsStore()
    const { multiPipelines } = usePipelineStore()
    const API_URL = process.env.REACT_APP_API_URL

    const [modalBtnLoading, setModalBtnLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [color, setColor] = useState('')
    const [subProgress, setSubProgress] = useState<SubProgress[]>([])
    const [approval, setApproval] = useState<Approval[]>([])
    const [templatePipelineOptions, setTemplatePipelineOptions] = useState<SelectOption[]>([])
    
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);

    const fetcher = async (url: string) => {
        const response = await axios.get(url, {
            headers: {
                'X-Auth-token': token,
            },
        });
        return response.data.data;
    };

    const { data: userOption = [], error: userOptionError, isLoading: userOptionLoading } = useSWR(`${API_URL}user/company`, fetcher)

    const userOptions = userOption.map((user: { id: string; name: string; }) => ({
        value: user.id,
        label: user.name
    }));

    const allOption = getAllOption(intl.formatMessage({ id: "FORM.LABEL.TEMPLATE" }, { title: settings.pipeline_title }), intl)

    const formik = useFormik({
        initialValues: {
            name: '',
            progressType: '',
            color: color,
            update_log_hpp_progress: false,
            isRequiredAttachment: false,
            isSplittable: false,
            accessTemplatePipeline: allOption,
            isRequiredApproval: false,
            editLeadProductAccess : {
                create: false,
                delete: false,
                edit: {
                    price: false,
                    quantity: false,
                    cogs: false,
                    discount: false,
                }
            }
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
            progressType: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROGRESS_TYPE" }) })),
            color: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.COLOR" }) })),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setModalBtnLoading(true)
            setSubmitting(true)

            const isApprovalRequired = approval.length > 0;
            values.isRequiredApproval = isApprovalRequired;

            const updatedApprovals = approval.map((item, index) => ({
                ...item,
                hierarchyLevel: index + 1
            }));

            const newDataSubProgress = subProgress.map(data => {
                const { id, ...rest } = data;
                return rest;
            });

            let accessTemplatePipeline = values.accessTemplatePipeline.map(item => item.value);
            if (values.accessTemplatePipeline.find(product => product.value === 'all')) accessTemplatePipeline = templatePipelineOptions.filter(item => item.value !== 'all').map(item => item.value);
            else accessTemplatePipeline = values.accessTemplatePipeline.map(item => item.value);

            const checkEmptyName = newDataSubProgress.some(data => data.name === "");
            const hasDuplicateName = newDataSubProgress.some((data, index, array) => array.findIndex(i => i.name === data.name) !== index);

            if (checkEmptyName) {
                Swal.fire({
                    icon: 'error',
                    text: intl.formatMessage({ id: 'FORM.VALIDATION.FIELD_REQUIRED' }, { title: intl.formatMessage({ id: 'FORM.LABEL.SUBPROGRESS' }) }),
                    heightAuto: false,
                    confirmButtonText: 'Ok'
                })

                setModalBtnLoading(false)
                setSubmitting(false)
                return false
            }

            if (hasDuplicateName) {
                Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: intl.formatMessage({ id: 'FORM.VALIDATION.DUPLICATE' }, { title: intl.formatMessage({ id: 'FORM.LABEL.SUBPROGRESS' }) }),
                    heightAuto: false,
                    confirmButtonText: 'Ok'
                })

                setModalBtnLoading(false)
                setSubmitting(false)
                return false
            }

            try {
                const accessProduct = {
                    create: values.editLeadProductAccess.create,
                    delete: values.editLeadProductAccess.delete,
                    edit: {
                        price: values.editLeadProductAccess.edit.price,
                        quantity: values.editLeadProductAccess.edit.quantity,
                        cogs: values.editLeadProductAccess.edit.cogs,
                        discount: values.editLeadProductAccess.edit.discount
                    }
                }
                const response = await insertProgress(
                    values.name, 
                    values.progressType, 
                    values.color, 
                    newDataSubProgress, 
                    token, 
                    accessTemplatePipeline, 
                    accessProduct, 
                    values.isRequiredAttachment, 
                    values.update_log_hpp_progress, 
                    values.isSplittable,
                    values.isRequiredApproval,
                    updatedApprovals,
                )
                if (response.data.success) {
                    Swal.fire({
                        icon: "success",
                        title: intl.formatMessage({ id: "ALERT.TITLE.SUCCESS" }),
                        text: intl.formatMessage({ id: "FORM.VALIDATION.ADD_SUCCESS" }),
                        heightAuto: false,
                    })
                    closeModalRef.current?.click();
                    // setTablesData('lead_progresses', ([...tablesData.lead_progresses, response.data.data]))
                    mutate(`${API_URL}lead_progresses`)
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.response.data.message,
                    confirmButtonText: 'Ok',
                    heightAuto: false,
                })
            } finally {
                setModalBtnLoading(false)
                setSubmitting(false)
            }
        }
    });

    useEffect(() => {
        // Get the modal element
        const modalElement = modalRef.current;

        // Define the event listener
        const handleModalHidden = () => {
            formik.resetForm()
            setShowModal(false)
            setSubProgress([])
            setApproval([])
        };

        // Attach the event listener
        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);

        // Cleanup
        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [formik]);

    useEffect(() => {
        let data = multiPipelines.map((pipeline: SettingMultiPipelines) => {
            return {
                value: pipeline.id,
                label: pipeline.name
            }
        })

        setTemplatePipelineOptions([...allOption, ...data])
    }, [multiPipelines])

    useEffect(() => {
        setColor(randomColor())
    }, [showModal, setShowModal])

    const progressTypes: SelectOption[] = [
        {
            value: "ONGOING",
            label: intl.formatMessage({ id: "PROGRESS.TYPE.ONGOING" })
        },
        {
            value: "SUCCESS",
            label: intl.formatMessage({ id: "PROGRESS.TYPE.SUCCESS" })
        },
        {
            value: "FAIL",
            label: intl.formatMessage({ id: "PROGRESS.TYPE.FAIL" })
        }
    ]

    const handleAddSubProgress = () => {
        setSubProgress([...subProgress, { id: '', name: '' }])
    }

    const handleChangeSubProgress = (value: string, key: number) => {
        subProgress.map((data: SubProgress, index: number) => {
            if (index === key) subProgress[index].name = value
        })

        setSubProgress([...subProgress]);
    }

    const handleDeleteSubProgress = (key: number) => {
        subProgress.splice(key, 1);
        setSubProgress([...subProgress]);
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5
            }
        })
    );

    const getAvailableUsers = (currentIndex: number) => {
        const alreadySelected = approval
            .filter((_, index) => index !== currentIndex)
            .flatMap(approvalItem => approvalItem.userId || []);
        
        return userOptions.filter((option: any) => !alreadySelected.includes(option.value));
    };

    const handleAddApproval = () => {
        setApproval([...approval, { userId: [], hierarchyLevel: 0 }]);
    }

    const handleChangeApproval = (value: string[], key: number, field: 'userId' | 'hierarchyLevel') => {
        const updatedApprovals = [...approval];
        updatedApprovals[key] = { ...updatedApprovals[key], [field]: value };
        setApproval(updatedApprovals);
    }

    const handleDeleteApproval = (key: number) => {
        approval.splice(key, 1);
        setApproval([...approval]);
    }

    const handleUserSelection = (selectedOptions: any, index: number) => {
        if (selectedOptions) {
            const userIds = selectedOptions.map((option: any) => option.value);
            handleChangeApproval(userIds, index, 'userId');
        } else {
            handleChangeApproval([], index, 'userId');
        }
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = parseInt(active.id);
            const newIndex = parseInt(over.id);
            const reorderedApprovals = arrayMove(approval, oldIndex, newIndex);
            const updatedApprovals = reorderedApprovals.map((item, index) => {
                return { ...item, hierarchyLevel: index + 1 };
            });
            setApproval(updatedApprovals);
        }
    };

    const SortableItem = ({ id, children }: { id: string, children: React.ReactNode }) => {
        const { setNodeRef, attributes, listeners, transform, isDragging } = useSortable({ id });

        const style = transform ? {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            opacity: isDragging ? 0.5 : 1,
            transition: "all 0.3s ease",
            backgroundColor: "#fff",
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            marginBottom: "0.75rem"
        } : {
            opacity: 1,
            transition: "all 0.3s ease",
            backgroundColor: "#fff",
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            marginBottom: "0.75rem"
        };

        return (
            <div
                ref={setNodeRef}
                style={style}
            >
                <div 
                    className="mx-2 cursor-move align-items-center"
                    {...attributes}
                    {...listeners}
                >
                    <i className="fas fa-grip-vertical text-gray-400"></i>
                </div>
                {children}
            </div>
        );
    };

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <div>
            <button type="button"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#tambah-progress-modal"
                onClick={() => setShowModal(true)}
            >
                <i className="fas fa-plus"></i> {intl.formatMessage({ id: 'FORM.ACTION.ADD' })} {intl.formatMessage({ id: 'FORM.MENU.PROGRESSES' })}
            </button>
            <div className="modal fade" tabIndex={-1} id="tambah-progress-modal" ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth:'800px' }}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.ADD' })} {intl.formatMessage({ id: 'FORM.MENU.PROGRESSES' })}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className="modal-body">
                                {formik.status && (
                                    <div className='mb-lg-15 alert alert-danger'>
                                        <div className='alert-text font-weight-bold'>{formik.status}</div>
                                    </div>
                                )}
                                <div className="row">
                                    <div className='col-12 col-md-8 px-4'>
                                        <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.NAME' })}</label>
                                        <div className="input-group mb-3">
                                            <input
                                                placeholder={intl.formatMessage({ id: 'FORM.MENU.PROGRESSES' })}
                                                {...formik.getFieldProps('name')}
                                                className={clsx(
                                                    'form-control form-control-lg',
                                                    { 'is-invalid': formik.touched.name && formik.errors.name },
                                                )}
                                                type='text'
                                                name='name'
                                                autoComplete='off'
                                                onInput={(e) => handleCaps(e)}
                                            />
                                        </div>
                                        {formik.touched.name && formik.errors.name && (
                                            <div className='fv-plugins-message-container text-danger'>
                                                <span role='alert' className="text-danger">{formik.errors.name}</span>
                                            </div>
                                        )}
                                        <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.PROGRESS_TYPE' })}</label>
                                        <VirtualizedSelect
                                            className="w-100 mb-3"
                                            options={progressTypes}
                                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.PROGRESS_TYPE' })}`}
                                            value={progressTypes.find(option => option.value === formik.values.progressType)}
                                            onChange={
                                                option => formik.handleChange({
                                                    target: {
                                                        name: "progressType",
                                                        value: option?.value
                                                    }
                                                })
                                            }
                                            onBlur={
                                                () => formik.values.progressType === "" && formik.setFieldTouched("progressType", true)
                                            }
                                        />
                                        {formik.touched.progressType && formik.errors.progressType && (
                                            <div className='fv-plugins-message-container text-danger'>
                                                <span role='alert' className="text-danger">{formik.errors.progressType}</span>
                                            </div>
                                        )}
                                        <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title })}</label>
                                        <div className="input-group mb-3">
                                            <VirtualizedSelect
                                                className='w-100'
                                                placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title })}`}
                                                value={formik.values.accessTemplatePipeline as any}
                                                options={templatePipelineOptions}
                                                onChange={(selectedOptions:VirtualizedSelectOption[]) => {
                                                    var endIndex = selectedOptions.length - 1
                                                    if (selectedOptions.length > 0 && selectedOptions[endIndex].value === 'all') {
                                                        formik.setFieldValue('accessTemplatePipeline', allOption)
                                                    } else {
                                                        formik.setFieldValue('accessTemplatePipeline', selectedOptions.filter(item => item.value !== 'all'))
                                                    }
                                                }}
                                                onBlur={
                                                    () => formik.values.accessTemplatePipeline.length === 0 && formik.setFieldTouched("accessTemplatePipeline", true)
                                                }
                                                isMulti
                                                isSort={false}
                                                closeMenuOnSelect={false}
                                                defaultComponentConfig={{
                                                    isMultiValuesMaxHeight: '200px'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className='col-12 col-md-4 px-4'>
                                        <div className="d-flex flex-column p-4 gap-2" style={{ border: '1px solid #E4E6EF' }}>

                                                <label className='form-label fs-6 fw-bolder text-dark required'>
                                                    {intl.formatMessage({ id: 'FORM.LABEL.COLOR' })}
                                                </label>
                                                <SketchPicker
                                                    color={formik.values.color}
                                                    onChange={(color) => {
                                                        formik.handleChange({
                                                            target: {
                                                                name: 'color',
                                                                value: color.hex,
                                                            },
                                                        })
                                                    }}
                                                    className='shadow-none border-0 p-0 w-100'
                                                />
                                            </div>
                                    </div>
                                </div>
                                <div className="separator separator-dashed my-6" />
                                {/* ---------------------------------- CONFIG: Toggle Add Log -------------------------------- */}
                                <label className='form-label fs-6 fw-bolder text-dark align-self-center mb-3'>{intl.formatMessage({ id: 'FORM.LABEL.CONFIG_TITLE' }, {title: intl.formatMessage({ id: 'FORM.LABEL.PROGRESS' })})}</label>
                                <TableToggleProgress formik={formik} />
                                {/* ---------------------------------- CONFIG: Sub Progress -------------------------------- */}
                                <div className="separator separator-dashed my-6" />
                                <div className="d-flex justify-content-between mb-3">
                                    <label className='form-label fs-6 fw-bolder text-dark mb-0 align-self-center'>{intl.formatMessage({ id: 'FORM.LABEL.SUBPROGRESS' })}</label>
                                    <button type="button" onClick={() => handleAddSubProgress()} className="btn btn-light-dark btn-rounded" style={{ padding: "5px 20px" }}>
                                        <i className="fas fa-plus pe-3"></i>
                                        {intl.formatMessage({ id: 'FORM.ACTION.ADD' })}
                                    </button>
                                </div>
                                {
                                    subProgress.map((data: SubProgress, index: number) => (
                                        <div className="mb-3 d-flex">
                                            <input
                                                className='form-control form-control-sm me-3'
                                                type='text'
                                                autoComplete='off'
                                                value={data.name}
                                                onChange={(e) => handleChangeSubProgress(e.target.value, index)}
                                                onInput={(e) => handleCaps(e)}
                                            />
                                            <button type="button" className="btn btn-icon btn-light-youtube" onClick={() => handleDeleteSubProgress(index)}>
                                                <i className="fas fa-trash fs-4"></i>
                                            </button>
                                        </div>
                                    ))
                                }
                                <div className="d-flex justify-content-between mb-3">
                                    <div className="d-flex flex-column">
                                        <label className='form-label fs-6 fw-bolder text-dark mb-0'>{intl.formatMessage({ id: 'FORM.LABEL.APPROVAL_LEVEL' })}</label>
                                        <label className='text-muted fs-7 mb-0'>{intl.formatMessage({ id: 'FORM.LABEL.APPROVAL_DESC' })}</label>
                                    </div>
                                    <button type="button" onClick={() => handleAddApproval()} className="btn btn-light-dark btn-rounded" style={{ padding: "5px 20px" }}>
                                        <i className="fas fa-plus pe-3"></i>
                                        {intl.formatMessage({ id: 'FORM.ACTION.ADD' })}
                                    </button>
                                </div>
                                <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
                                    <SortableContext items={approval.map((_, index) => index.toString())} strategy={verticalListSortingStrategy}>
                                        {
                                            approval.map((data: Approval, index: number) => (
                                                <SortableItem key={index} id={index.toString()}>
                                                    <div className="d-flex w-100 align-items-center">
                                                        <div 
                                                            className="d-flex align-items-center justify-content-center me-3 rounded-circle text-white fw-bold"
                                                            style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                backgroundColor: '#797b85',
                                                                fontSize: '14px',
                                                                minWidth: '32px'
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </div>
                                                        <Select
                                                            isLoading={userOptionLoading}
                                                            styles={{
                                                                control: (baseStyles) => ({
                                                                    ...baseStyles,
                                                                    border: "1px solid #E4E6EF",
                                                                    backgroundColor: "none",
                                                                }),
                                                                option: (baseStyles, { isSelected, isFocused }) => ({
                                                                    ...baseStyles,
                                                                    backgroundColor: isSelected ? '#2684FF' : isFocused ? '#dcf2ff' : 'none',
                                                                    cursor: 'pointer'
                                                                }),
                                                                indicatorSeparator: (baseStyles) => ({
                                                                    ...baseStyles,
                                                                    display: 'none',
                                                                }),
                                                            }}
                                                            theme={(theme) => ({
                                                                ...theme,
                                                                colors: {
                                                                    ...theme.colors,
                                                                    primary: 'none',
                                                                },
                                                            })}
                                                            isMulti={true}
                                                            options={getAvailableUsers(index)}
                                                            onChange={(selectedOptions) => handleUserSelection(selectedOptions, index)}
                                                            value={userOptions.filter((option: { value: string }) => 
                                                                (data.userId || []).includes(option.value))
                                                            }
                                                            className='react-select-styled w-100 me-3'
                                                            placeholder={intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' }) + " User"}
                                                            closeMenuOnSelect={false}
                                                        />
                                                        <button type="button" className="btn btn-icon btn-light-youtube" onClick={() => handleDeleteApproval(index)}>
                                                            <i className="fas fa-trash fs-4"></i>
                                                        </button>
                                                    </div>
                                                </SortableItem>
                                            ))
                                        }
                                    </SortableContext>
                                </DndContext>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light d-none"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? "disabled" : ""}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                    <span className="indicator-label">
                                        {intl.formatMessage({ id: 'FORM.ACTION.ADD' })}
                                    </span>
                                    <span className="indicator-progress">
                                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export { AddProgress }